/**
 * Custom User Menu
 *
 * Dropdown menu showing user information and actions:
 * - User avatar, name and email
 * - Contacts role badge
 * - Logout button (ends the SSO session and lands on the portal)
 *
 * Based on the Roommatik design system. No session countdown: the SSO
 * provider refreshes the token silently while the session is alive.
 */

import React from 'react';
import {
  UserMenu,
  UserMenuProps,
  useTranslate,
  useGetIdentity,
  usePermissions,
  useLogout,
} from 'react-admin';
import {
  Box,
  Typography,
  Button,
  Avatar,
  Divider,
  Chip,
  Paper,
  Stack,
} from '@mui/material';
import {
  Person as PersonIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { USER_ROLES } from '@/config/constants';

interface MyUserMenuProps extends Omit<UserMenuProps, 'children'> {}

/** Display names for the Contacts roles */
const getRoleName = (role: string): string => {
  const roleNames: Record<string, string> = {
    [USER_ROLES.SUPERUSER]: 'Superusuario',
    [USER_ROLES.COMERCIAL]: 'Comercial',
    [USER_ROLES.MARKETING]: 'Marketing',
  };
  return roleNames[role] || role;
};

const getRoleColor = (role: string): 'primary' | 'secondary' | 'default' => {
  const colors: Record<string, 'primary' | 'secondary' | 'default'> = {
    [USER_ROLES.SUPERUSER]: 'primary',
    [USER_ROLES.COMERCIAL]: 'secondary',
    [USER_ROLES.MARKETING]: 'secondary',
  };
  return colors[role] || 'default';
};

export const MyUserMenu: React.FC<MyUserMenuProps> = (props) => {
  const translate = useTranslate();
  const { data: identity, isLoading } = useGetIdentity();
  const { permissions } = usePermissions<string>();
  const logout = useLogout();

  const handleLogout = () => {
    void logout();
  };

  if (isLoading) {
    return <UserMenu {...props} />;
  }

  const userName = identity?.fullName || 'Usuario';
  const userEmail = (identity as { email?: string } | undefined)?.email ?? '';
  const userRole = permissions ?? '';

  return (
    <UserMenu {...props}>
      <Paper
        sx={{
          p: 2,
          width: 280,
          borderRadius: 2,
        }}
        elevation={0}
      >
        <Stack spacing={2}>
          {/* Header with Avatar and User Info */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pb: 1 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'grey.300',
                mb: 2,
              }}
            >
              <PersonIcon sx={{ fontSize: 32, color: 'grey.600' }} />
            </Avatar>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {userName}
            </Typography>

            {userEmail && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {userEmail}
              </Typography>
            )}

            {/* Role chip */}
            {userRole && (
              <Chip
                label={getRoleName(userRole)}
                size="small"
                color={getRoleColor(userRole)}
                sx={{ mb: 1 }}
              />
            )}
          </Box>

          <Divider />

          {/* Action buttons */}
          <Stack spacing={1}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              {translate('ra.auth.logout', { _: 'Logout' })}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </UserMenu>
  );
};

export default MyUserMenu;
